import { MRTGrid, type IMRTGridProps } from "@foodpilot/foods";
import type { SxProps, TableContainerProps, Theme } from "@mui/material";
import type { MRT_RowData } from "material-react-table";

import { hubGridIntrinsicHeaderSx } from "./layout/hubGridIntrinsicHeader";

type SxStyleObject = Record<string, unknown>;
type TableContainerPropsFactory<Data extends MRT_RowData> = Exclude<
    NonNullable<IMRTGridProps<Data>["muiTableContainerProps"]>,
    TableContainerProps
>;

const isSxStyleObject = (value: unknown): value is SxStyleObject =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const mergeSxObjects = (baseStyles: SxStyleObject, overrideStyles: SxStyleObject): SxStyleObject =>
    Object.entries(overrideStyles).reduce<SxStyleObject>(
        (mergedStyles, [key, value]) => {
            const baseValue = mergedStyles[key];
            mergedStyles[key] =
                isSxStyleObject(baseValue) && isSxStyleObject(value) ? mergeSxObjects(baseValue, value) : value;
            return mergedStyles;
        },
        { ...baseStyles },
    );

const resolveSxObject = (sx: unknown, theme: Theme): SxStyleObject => {
    if (sx === undefined || sx === null || sx === false) return {};
    if (typeof sx === "function") return resolveSxObject((sx as (theme: Theme) => unknown)(theme), theme);
    if (Array.isArray(sx)) {
        return sx.reduce<SxStyleObject>(
            (resolvedStyles, entry) => mergeSxObjects(resolvedStyles, resolveSxObject(entry, theme)),
            {},
        );
    }

    return isSxStyleObject(sx) ? sx : {};
};

const hubOpaquePinnedCellsSx = { '& [data-pinned="true"]': { opacity: 1 } } as const;

const withHubTableOverrides = (props: TableContainerProps = {}): TableContainerProps => ({
    ...props,
    // MRT spreads the resolved `sx` value into an object. Passing a standard
    // MUI sx array therefore turns its entries into numeric keys and silently
    // drops every CSS selector. Resolve to one object at this boundary.
    sx: ((theme: Theme) =>
        mergeSxObjects(
            mergeSxObjects(resolveSxObject(props.sx, theme), hubGridIntrinsicHeaderSx),
            hubOpaquePinnedCellsSx,
        )) as SxProps<Theme>,
});

/**
 * Application boundary around Foods MRTGrid.
 *
 * Foods 2.20.1 fixes hub headers to the 64 px two-line example. Product V2
 * headers are content-driven, so the compatibility override lives here once
 * until Foods exposes intrinsic hub headers upstream.
 */
export const AppMRTGrid = <Data extends MRT_RowData>(props: IMRTGridProps<Data>) => {
    const { appearance, muiTableContainerProps, ...tableProps } = props;
    const resolvedTableContainerProps =
        appearance === "hub" ?
            typeof muiTableContainerProps === "function" ?
                (factoryProps: Parameters<TableContainerPropsFactory<Data>>[0]) =>
                    withHubTableOverrides(muiTableContainerProps(factoryProps))
            :   withHubTableOverrides(muiTableContainerProps)
        :   muiTableContainerProps;

    return (
        <MRTGrid<Data> {...tableProps} appearance={appearance} muiTableContainerProps={resolvedTableContainerProps} />
    );
};
